import type { RecordingChunk } from '@/types'
import { db } from './database'
import { hydrateBlob, serializeChunkBlob } from './blob'
import { runDbOperation } from './errors'
import {
  isStoragePressureError,
  logSyncTelemetry,
  toTelemetryErrorFields,
} from '../sync/telemetry'

export function normalizeChunkLedgerFields(
  chunk: RecordingChunk,
): RecordingChunk {
  const nowIso = new Date().toISOString()
  const ackStatus =
    chunk.ackStatus ??
    (chunk.uploadStatus === 'uploaded' || chunk.uploadedAt
      ? 'acknowledged'
      : chunk.uploadStatus === 'failed'
        ? 'failed'
        : 'pending')
  const retryCount = chunk.retryCount ?? chunk.uploadAttempts ?? 0
  const updatedAt = chunk.updatedAt ?? nowIso
  const acknowledgedAt =
    chunk.acknowledgedAt ??
    (ackStatus === 'acknowledged' ? chunk.uploadedAt ?? updatedAt : null)

  return {
    ...chunk,
    recordingSessionId: chunk.recordingSessionId ?? chunk.sessionId,
    chunkIndex: chunk.chunkIndex ?? chunk.index,
    ackStatus,
    retryCount,
    uploadAttempts: chunk.uploadAttempts ?? retryCount,
    queuedAt: chunk.queuedAt ?? chunk.createdAt ?? nowIso,
    enqueuedAt: chunk.enqueuedAt ?? null,
    lastUploadAttemptAt: chunk.lastUploadAttemptAt ?? null,
    lastRetryAt: chunk.lastRetryAt ?? null,
    acknowledgedAt,
    updatedAt,
    tusUploadUrl: chunk.tusUploadUrl ?? null,
  }
}

function hydrateChunk(chunk: RecordingChunk): RecordingChunk {
  const normalized = normalizeChunkLedgerFields(chunk)
  if (!normalized.blob && normalized.blobData) {
    normalized.blob =
      hydrateBlob(normalized.blobData, normalized.blobType) || undefined
  }
  return normalized
}

export async function saveRecordingChunk(
  chunk: RecordingChunk,
): Promise<void> {
  return runDbOperation(async () => {
    try {
      const toStore: RecordingChunk = normalizeChunkLedgerFields({ ...chunk })
      const { data, type } = await serializeChunkBlob(chunk.blob, chunk.blobType)
      toStore.blob = null as any
      toStore.blobData = data
      toStore.blobType = type
      await db.chunks.put(toStore)
    } catch (error) {
      if (isStoragePressureError(error)) {
        logSyncTelemetry({
          type: 'storage-pressure',
          scope: 'chunks',
          operation: 'saveRecordingChunk',
          itemId: chunk.id,
          ...toTelemetryErrorFields(error),
        })
      }
      throw error
    }
  })
}

export async function getChunksBySession(
  sessionId: string,
): Promise<RecordingChunk[]> {
  return runDbOperation(async () => {
    const chunks = await db.chunks
      .where('sessionId')
      .equals(sessionId)
      .toArray()
    return chunks.map(hydrateChunk).sort((a, b) => a.index - b.index)
  })
}

export async function deleteChunksBySession(
  sessionId: string,
): Promise<void> {
  return runDbOperation(async () => {
    await db.chunks.where('sessionId').equals(sessionId).delete()
  })
}

export async function clearAllChunks(): Promise<void> {
  await db.chunks.clear()
}

export async function getAllChunkSessions(): Promise<string[]> {
  return runDbOperation(async () => {
    const keys = await db.chunks.orderBy('sessionId').uniqueKeys()
    return keys.filter((k): k is string => typeof k === 'string')
  })
}

export async function getAllSubmissionSessionIds(): Promise<string[]> {
  return runDbOperation(async () => {
    const all = await db.submissions.toArray()
    const ids: string[] = []
    for (const s of all) {
      if (s.id) ids.push(s.id)
      if (s.chunkSessionId) ids.push(s.chunkSessionId)
    }
    return ids
  })
}

export async function updateRecordingChunkMeta(
  id: string,
  updates: Partial<RecordingChunk>,
): Promise<RecordingChunk | null> {
  return runDbOperation(async () => {
    const existing = await db.chunks.get(id)
    if (!existing) {
      return null
    }
    const updated = normalizeChunkLedgerFields({
      ...(existing as RecordingChunk),
      ...updates,
    })
    await db.chunks.put(updated)
    return hydrateChunk(updated)
  })
}

export async function enqueueRecordingChunkUpload(
  id: string,
): Promise<RecordingChunk | null> {
  const nowIso = new Date().toISOString()
  return updateRecordingChunkMeta(id, {
    uploadStatus: 'pending',
    ackStatus: 'pending',
    enqueuedAt: nowIso,
    updatedAt: nowIso,
  })
}
